import { CircularProgress, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { ProhibitIcon } from "@phosphor-icons/react/dist/csr/Prohibit";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import type { FlowSummary } from "../../graphql/types";

const getSummaryInfo = (flowSummary?: FlowSummary | null) => {
  const failed = flowSummary?.failed || 0 > 0;
  const allDirty = flowSummary?.dirty_rows_all || 0 > 0;
  const someDirty = flowSummary?.dirty_rows_some || 0 > 0;
  const unfinished = flowSummary?.unfinished || 0 > 0;
  const succeeded = flowSummary?.succeeded || 0 > 0;

  return {
    failed,
    allDirty,
    someDirty,
    unfinished,
    succeeded,
  };
};

const TooltipContent = ({ flowSummary }: { flowSummary?: FlowSummary | null }) => {
  const messages: string[] = [];

  const { failed, allDirty, someDirty, unfinished, succeeded } = getSummaryInfo(flowSummary);

  if (failed) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    messages.push(`${flowSummary!.failed} failed tests.`);
  }
  if (allDirty) {
    messages.push(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      `${flowSummary!.dirty_rows_all} failed tests with 100% dirty rows.`
    );
  }
  if (someDirty) {
    messages.push(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      `${flowSummary!.dirty_rows_some} failed tests with some dirty rows.`
    );
  }
  if (unfinished) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    messages.push(`${flowSummary!.unfinished} tests in progress.`);
  }
  if (succeeded) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    messages.push(`${flowSummary!.succeeded} completed successfully.`);
  }

  if (!messages.length) {
    return <Typography component="p">No tests active.</Typography>;
  }

  return (
    <Stack spacing={0.5}>
      <Typography component="p" sx={{ fontWeight: "bold" }}>
        Test summary:
      </Typography>
      {messages.map((message, index) => (
        <Typography key={index} component="p">
          {message}
        </Typography>
      ))}
    </Stack>
  );
};

const FlowSummaryIcon = ({ flowSummary }: { flowSummary?: FlowSummary | null }) => {
  const theme = useTheme();

  let icon = <ProhibitIcon color={theme.palette.text40.main} size="18px" data-testid="flow-summary-icon" />;

  const { failed, allDirty, someDirty, unfinished, succeeded } = getSummaryInfo(flowSummary);

  if (failed || allDirty) {
    icon = <WarningIcon color={theme.palette.accentDanger30.main} size="18px" data-testid="flow-summary-icon" />;
  } else if (someDirty) {
    icon = <WarningIcon color={theme.palette.accentWarning30.main} size="18px" data-testid="flow-summary-icon" />;
  } else if (unfinished) {
    icon = <CircularProgress size="1em" data-testid="flow-summary-icon" />;
  } else if (succeeded) {
    icon = <CheckIcon color={theme.palette.accentSuccess30.main} size="18px" data-testid="flow-summary-icon" />;
  }
  return (
    <Tooltip id="flow-summary-tooltip" title={<TooltipContent flowSummary={flowSummary} />} placement="right" arrow>
      {icon}
    </Tooltip>
  );
};

export default FlowSummaryIcon;
